import { URL } from '../store/index'
import { normalizeSiloLastProfile } from './siloLevelToKg'

export const resolvePk = (value) => {
  if (value == null) return null
  if (typeof value === 'object') return value.pk ?? value.id ?? null
  return value
}

export const isOpenBatch = (batch) => {
  if (!batch) return false
  if (batch.is_closed === true) return false
  return batch.date_departure == null
}

export const hasOpenPriorityBatch = (batch) => {
  if (!batch || batch.isPriorityStub) return false
  if (batch.pk == null) return false
  return isOpenBatch(batch)
}

export const filterFeedByVirtual = (sensors) => {
  if (!Array.isArray(sensors)) return []
  const siloGroups = {}
  sensors.forEach(sensor => {
    const siloId = sensor.silo?.pk ?? sensor.silo
    if (!siloGroups[siloId]) siloGroups[siloId] = []
    siloGroups[siloId].push(sensor)
  })
  return sensors.filter(sensor => {
    const siloId = sensor.silo?.pk ?? sensor.silo
    const group = siloGroups[siloId] || []
    return group.length === 1 || sensor.virtual
  })
}

export const resolveBarnPkForBatch = (batch, allBarns = []) => {
  const barnObj = batch?.barn
  if (barnObj && typeof barnObj === 'object' && barnObj.pk != null) {
    return barnObj.pk
  }

  for (const entry of allBarns) {
    const barns = entry?.barn || []
    for (const barn of barns) {
      const batchPk = barn?.batch?.pk ?? barn?.batch?.id
      if (batchPk != null && batch?.pk != null && String(batchPk) === String(batch.pk)) {
        return barn.pk
      }
    }
  }

  return resolvePk(barnObj)
}

export const enrichEntryFromAllBarns = (entry, allBarns = []) => {
  const barnPk = entry?.barn?.pk
  if (barnPk == null) return entry

  for (const item of allBarns) {
    const barns = item?.barn || []
    for (const barn of barns) {
      if (barn.pk !== barnPk) continue
      entry.barn.name = barn.name || entry.barn.name
      const farm = item.farm
      entry.farm = farm?.farm?.pk ?? farm?.farm ?? farm?.pk ?? entry.farm
      entry.farm_name = farm?.name || entry.farm_name
      return entry
    }
  }

  return entry
}

export const normalizeFeedInventoryBatch = (batch, allBarns = []) => {
  const barnPk = resolveBarnPkForBatch(batch, allBarns)
  if (!barnPk) return null

  const farmPk = resolvePk(batch.farm)
  const barnName = typeof batch.barn === 'object' ? batch.barn?.name : ''
  const farmName = batch.farm_name || batch.farm?.name || ''

  return enrichEntryFromAllBarns({
    ...batch,
    barn: { pk: barnPk, name: barnName },
    farm: farmPk,
    farm_name: farmName
  }, allBarns)
}

export const resolveBarnIdFromBatch = (batch) => {
  if (!batch) return null
  const barn = batch.barn
  if (barn == null) return null
  if (typeof barn === 'number' || typeof barn === 'string') return Number(barn)
  return barn.pk ?? barn.id ?? null
}

export const resolveBiLastDayConsume = (consumeData) => {
  if (!Array.isArray(consumeData) || !consumeData.length) return 0

  for (let i = consumeData.length - 1; i >= 0; i -= 1) {
    const consume = resolveEntryConsume(consumeData[i])
    if (consume > 0) return consume
  }

  return 0
}

export const findBatchForBarnInOpenBatches = (barnPk, openBatches = []) => {
  if (barnPk == null || !Array.isArray(openBatches)) return null

  for (const batch of openBatches) {
    if (!isOpenBatch(batch)) continue
    const batchBarnPk = resolvePk(batch.barn)
    if (batchBarnPk != null && String(batchBarnPk) === String(barnPk)) {
      return batch
    }
  }

  return null
}

export const resolvePriorityStockTotal = async ({
  barnIds,
  getBarnSensors,
  fetchPaginated,
  headers
}) => {
  let stockTotal = barnIds.reduce(
    (sum, barnId) => sum + sumSensorStock(getBarnSensors(barnId)),
    0
  )

  if (stockTotal > 0) return stockTotal

  for (const barnId of barnIds) {
    const fromApi = await sumBiStockForBarn(fetchPaginated, headers, barnId)
    stockTotal += fromApi
  }

  return stockTotal
}

export const calculateBiFeedDaysRemaining = (stockTotal, consumeData) => {
  const stock = Number(stockTotal ?? 0)
  const consumoDiario = resolveBiLastDayConsume(consumeData)

  if (stock <= 0 || consumoDiario <= 0) return 0

  return Math.round(stock / consumoDiario)
}

export const sumBiStockForBarn = async (fetchPaginated, headers, barnId) => {
  if (barnId == null) return 0

  let sensors = await fetchPaginated({
    url: `${URL}sensors/?type=smartfeed&status=installed&barn=${barnId}`
  })

  if (!Array.isArray(sensors) || !sensors.length) {
    sensors = await fetchPaginated({
      url: `${URL}sensors/?type=smartfeed&barn=${barnId}`
    })
  }

  if (!Array.isArray(sensors) || !sensors.length) return 0

  const filtered = filterFeedByVirtual(sensors)
  const enriched = await Promise.all(
    filtered.map(sensor => enrichSensorWithStockData(headers, sensor))
  )

  return enriched.reduce(
    (sum, sensor) => sum + resolveEffectiveSensorWeight(sensor),
    0
  )
}

export const resolveBiLastDayConsumeExact = (consumeData) => {
  if (!Array.isArray(consumeData) || !consumeData.length) return 0
  const ultimoDia = consumeData[consumeData.length - 1]
  return resolveEntryConsume(ultimoDia)
}

export const computeBiDeliveryPriorityDays = async ({
  batchId,
  barnId,
  headers,
  fetchPaginated,
  getBarnSensors,
  batch = null,
  allBarns = [],
  cachedBarnData = null
}) => {
  if (barnId == null) return 0

  let stockTotal = 0
  const cachedSensors = getBarnSensors?.(barnId) || []
  if (cachedSensors.length) {
    stockTotal = sumSensorStock(cachedSensors)
  }

  if (stockTotal <= 0) {
    stockTotal = await sumBiStockForBarn(fetchPaginated, headers, barnId)
  }

  if (stockTotal <= 0) return 0

  let consumeData = []
  if (batchId) {
    consumeData = await fetchConsumeByBatchReport(headers, batchId)
  }

  let consumoDiario = resolveBiLastDayConsumeExact(consumeData)
  if (consumoDiario <= 0) {
    consumoDiario = resolveBiLastDayConsume(consumeData)
  }

  if (consumoDiario <= 0 && batch) {
    consumeData = await fetchPriorityConsumeData({
      headers,
      batch,
      barnPk: barnId,
      allBarns,
      cachedBarnData
    })
    consumoDiario = resolveBiLastDayConsumeExact(consumeData)
    if (consumoDiario <= 0) {
      consumoDiario = resolveBiLastDayConsume(consumeData)
    }
  }

  if (consumoDiario <= 0) return 0

  return Math.round(stockTotal / consumoDiario)
}

export const resolveBatchAccommodationDate = (batch) => {
  if (!batch) return null
  return batch.date_accomodation ?? batch.date_accommodation ?? null
}

export const coerceFeedInventoryBatch = (batch, allBarns = []) => {
  if (!batch) return null

  const normalized = normalizeFeedInventoryBatch(batch, allBarns)
  if (normalized) {
    const accommodation = resolveBatchAccommodationDate(batch)
    if (accommodation && !normalized.date_accomodation) {
      normalized.date_accomodation = accommodation
    }
    return normalized
  }

  const batchPk = resolvePk(batch)
  const barnPk = resolvePk(batch?.barn)
  if (batchPk == null && barnPk == null && batch?.farm == null && !batch?.farm_name) {
    return null
  }

  const accommodation = resolveBatchAccommodationDate(batch)

  return {
    ...batch,
    pk: batchPk,
    barn: {
      pk: barnPk,
      name: typeof batch.barn === 'object' ? batch.barn?.name : ''
    },
    farm: resolvePk(batch.farm),
    farm_name: batch.farm_name || batch.farm?.name || '',
    date_accomodation: accommodation,
    date_accommodation: accommodation
  }
}

export const dedupeOpenBatchesByFarm = (batches, allBarns = []) => {
  const uniqueFarms = new Set()
  const entries = []

  for (const batch of batches) {
    if (!isOpenBatch(batch)) continue

    const normalized = normalizeFeedInventoryBatch(batch, allBarns)
    if (!normalized) continue

    const farmKey = resolveFarmKey(normalized)
    if (uniqueFarms.has(farmKey)) continue
    uniqueFarms.add(farmKey)
    entries.push(normalized)
  }

  return entries
}

export const extractFarmBaseName = (name) => {
  return String(name || '')
    .split(/\s[-–—/]\s/)[0]
    .toLowerCase()
    .trim()
}

export const farmNamesMatch = (leftName, rightName) => {
  const left = extractFarmBaseName(leftName)
  const right = extractFarmBaseName(rightName)
  if (!left || !right) return false
  return left === right
}

export const resolveFarmKey = (batchOrName, nameToPk = null) => {
  if (batchOrName == null) return ''

  if (typeof batchOrName === 'string') {
    const baseName = extractFarmBaseName(batchOrName)
    if (nameToPk?.has(baseName)) return nameToPk.get(baseName)
    return baseName
  }

  if (batchOrName.farm != null) return String(batchOrName.farm)

  if (batchOrName.farm_name) {
    const baseName = extractFarmBaseName(batchOrName.farm_name)
    if (nameToPk?.has(baseName)) return nameToPk.get(baseName)
    return baseName
  }

  return String(batchOrName.pk ?? '')
}

export const buildFarmNameToPkMap = ({
  companyBarns = [],
  openBatches = [],
  allBarns = []
} = {}) => {
  const nameToPk = new Map()

  const register = (farmPk, farmName) => {
    if (farmPk == null || !farmName) return
    nameToPk.set(extractFarmBaseName(farmName), String(farmPk))
  }

  for (const entry of allBarns) {
    const farmPk = entry?.farm?.farm?.pk ?? entry?.farm?.farm ?? entry?.farm?.pk ?? null
    const farmName = entry?.farm?.name || entry?.farm?.farm?.name || ''
    register(farmPk, farmName)
  }

  for (const batch of openBatches) {
    register(batch.farm ?? resolvePk(batch.farm), batch.farm_name)
  }

  for (const barn of companyBarns) {
    const barnBaseName = extractFarmBaseName(barn.farmName)
    if (nameToPk.has(barnBaseName)) continue

    for (const entry of allBarns) {
      const farmName = entry?.farm?.name || entry?.farm?.farm?.name || ''
      if (!farmNamesMatch(farmName, barn.farmName)) continue
      const farmPk = entry?.farm?.farm?.pk ?? entry?.farm?.farm ?? entry?.farm?.pk ?? null
      register(farmPk, barn.farmName)
      break
    }
  }

  return nameToPk
}

export const findOpenBatchForFarmName = (farmName, openBatches, allBarns = [], nameToPk = null) => {
  if (!farmName) return null

  const farmKey = resolveFarmKey(farmName, nameToPk)

  for (const batch of openBatches) {
    if (!isOpenBatch(batch)) continue
    if (resolveFarmKey(batch, nameToPk) === farmKey) {
      return coerceFeedInventoryBatch(batch, allBarns)
    }
    if (farmNamesMatch(batch.farm_name, farmName)) {
      return coerceFeedInventoryBatch(batch, allBarns)
    }
  }

  return null
}

export const findOpenBatchForBarnPk = (barnPk, openBatches, allBarns = [], nameToPk = null) => {
  if (barnPk == null) return null

  for (const batch of openBatches) {
    if (!isOpenBatch(batch)) continue
    const batchBarnPk = resolveBatchBarnPk(batch, allBarns)
    if (String(batchBarnPk) !== String(barnPk)) continue
    return coerceFeedInventoryBatch(batch, allBarns)
  }

  for (const entry of allBarns) {
    for (const barn of entry?.barn || []) {
      if (String(barn.pk) !== String(barnPk)) continue
      const batchRef = barn.batch
      if (!batchRef || !isOpenBatch(batchRef)) break

      const batchPk = resolvePk(batchRef)
      const existing = openBatches.find(item => String(item.pk) === String(batchPk))
      if (existing) {
        return coerceFeedInventoryBatch(existing, allBarns)
      }

      return coerceFeedInventoryBatch(
        { ...batchRef, barn: { pk: barnPk, name: barn.name } },
        allBarns
      )
    }
  }

  const barnMeta = allBarns.flatMap(entry => {
    const farmName = entry?.farm?.name || entry?.farm?.farm?.name || ''
    return (entry?.barn || []).map(barn => ({ barn, farmName }))
  }).find(item => String(item.barn.pk) === String(barnPk))

  if (barnMeta?.farmName) {
    return findOpenBatchForFarmName(barnMeta.farmName, openBatches, allBarns, nameToPk)
  }

  return null
}

export const findOpenBatchForBarnRelaxed = (
  barnPk,
  openBatches,
  allBarns = [],
  companyBarns = [],
  nameToPk = null
) => {
  const direct = findOpenBatchForBarnPk(barnPk, openBatches, allBarns, nameToPk)
  if (direct) return direct

  const barnMeta = companyBarns.find(b => String(b.pk) === String(barnPk))
  if (!barnMeta?.farmName) return null

  return findOpenBatchForFarmName(barnMeta.farmName, openBatches, allBarns, nameToPk)
}

export const createPriorityBatchStub = ({ barnPk, barnName, farmName, barnLabel }) => ({
  pk: null,
  priorityKey: `barn-${barnPk}`,
  isPriorityStub: true,
  barn: { pk: barnPk, name: barnName || '' },
  farm_name: farmName || barnLabel || barnName || '',
  date_accomodation: null
})

export const resolvePriorityBatchKey = (batch) => {
  if (!batch) return null
  if (batch.pk != null) return batch.pk
  return batch.priorityKey ?? null
}

export const fetchOpenBatchForBarn = async (headers, barnPk, allBarns = []) => {
  if (barnPk == null) return null

  try {
    const response = await fetch(
      `${URL}batches/report/home/barn=${barnPk}`,
      { headers }
    )
    if (!response.ok) return null

    const json = await response.json()
    const batches = Array.isArray(json) ? json : [json]
    const openBatch = batches.find(isOpenBatch)
    if (!openBatch) return null

    return coerceFeedInventoryBatch(openBatch, allBarns) || {
      ...openBatch,
      pk: resolvePk(openBatch)
    }
  } catch {
    return null
  }
}

export const buildAllBarnsPriorityEntries = ({
  siloStockByBarn = [],
  companyBarns = [],
  openBatches = [],
  allBarns = []
}) => {
  const nameToPk = buildFarmNameToPkMap({ companyBarns, openBatches, allBarns })
  const entries = []
  const seenKeys = new Set()

  for (const group of siloStockByBarn) {
    const entryKey = `barn-${group.barnPk}`
    if (seenKeys.has(entryKey)) continue
    seenKeys.add(entryKey)

    const barnMeta = companyBarns.find(b => String(b.pk) === String(group.barnPk))
    const farmName = barnMeta?.farmName
      || String(group.barnLabel || '').split(/\s[-–—/]\s/)[0]?.trim()
      || ''

    let batch = findBatchForBarnInOpenBatches(group.barnPk, openBatches)
      || findOpenBatchForBarnRelaxed(
        group.barnPk,
        openBatches,
        allBarns,
        companyBarns,
        nameToPk
      )

    if (!batch) continue

    batch = {
      ...batch,
      barn: resolveBarnIdFromBatch(batch) ?? group.barnPk
    }

    entries.push({
      entryKey,
      batch,
      stockBarnPks: [group.barnPk],
      displayLabel: group.barnLabel || farmName
    })
  }

  for (const batch of dedupeOpenBatchesByFarm(openBatches, allBarns)) {
    const barnPk = resolveBatchBarnPk(batch, allBarns)
    const farmKey = resolveFarmKey(batch, nameToPk)
    const entryKey = barnPk != null ? `barn-${barnPk}` : `farm-${farmKey}`

    if (seenKeys.has(entryKey)) continue
    seenKeys.add(entryKey)

    const normalized = coerceFeedInventoryBatch(batch, allBarns) || batch
    const farmName = String(normalized.farm_name || '').trim()
    const barnName = String(normalized.barn?.name || '').trim()
    const displayLabel = farmName && barnName
      ? `${farmName} — ${barnName}`
      : (farmName || barnName)

    entries.push({
      entryKey,
      batch: normalized,
      stockBarnPks: barnPk != null ? [barnPk] : [],
      displayLabel
    })
  }

  return entries
}

export const collectCompanyBarns = (batches, allBarns = [], companyId = null) => {
  const barnMap = new Map()

  const addBarn = (pk, name, farmName) => {
    if (pk == null) return
    const key = String(pk)
    const current = barnMap.get(key)
    barnMap.set(key, {
      pk,
      name: name || current?.name || `Galpão ${pk}`,
      farmName: farmName || current?.farmName || ''
    })
  }

  for (const batch of batches) {
    if (!isOpenBatch(batch)) continue

    const normalized = normalizeFeedInventoryBatch(batch, allBarns)
    if (!normalized) continue

    addBarn(
      normalized.barn?.pk,
      normalized.barn?.name,
      normalized.farm_name
    )
  }

  for (const entry of allBarns) {
    const entryCompany = entry?.farm?.company ?? entry?.farm?.farm?.company ?? null
    if (companyId != null && entryCompany != null && String(entryCompany) !== String(companyId)) {
      continue
    }

    const farmName = entry?.farm?.name || entry?.farm?.farm?.name || ''
    for (const barn of entry?.barn || []) {
      addBarn(barn?.pk, barn?.name, farmName)
    }
  }

  return Array.from(barnMap.values()).sort((a, b) => {
    const labelA = `${a.farmName} ${a.name}`.trim().toLowerCase()
    const labelB = `${b.farmName} ${b.name}`.trim().toLowerCase()
    return labelA.localeCompare(labelB, 'pt-BR')
  })
}

export const resolveSensorPk = (sensor) => sensor?.pk ?? sensor?.sensor ?? sensor?.id ?? null

export const resolveSiloPk = (sensor) => sensor?.silo?.pk ?? sensor?.silo?.id ?? sensor?.silo ?? null

export const resolveSensorReadingDate = (sensor) => {
  if (!sensor) return null
  return sensor.datetime
    ?? sensor.datetime_last_data
    ?? sensor.last_updated
    ?? sensor.datetime_fetched
    ?? null
}

export const fetchSiloProfileData = async (headers, siloPk) => {
  if (siloPk == null) return null

  try {
    const response = await fetch(
      `${URL}silo_profiles/silo_last_profiles_by_silo/${siloPk}/?silo_dimensions_only=true`,
      { headers }
    )
    if (!response.ok) return null
    const profile = await response.json()
    if (profile?.detail) return null
    return normalizeSiloLastProfile(profile)
  } catch {
    return null
  }
}

export const enrichSensorWithStockData = async (headers, sensor) => {
  const sensorPk = resolveSensorPk(sensor)
  const siloPk = resolveSiloPk(sensor)
  const profile = await fetchSiloProfileData(headers, siloPk)
  if (!profile) return sensor

  const merged = {
    ...sensor,
    pk: sensorPk,
    silo: siloPk,
    name: profile.silo_name || sensor.name,
    last_hour_weight: Number(profile.weight ?? 0),
    last_hour_level: Number(profile.level ?? 0),
    informed_max_weight: Number(sensor.informed_max_weight ?? profile.informed_max_weight ?? 0),
    datetime: profile.last_updated || profile.datetime_fetched || profile.datetime || sensor.datetime || null,
    last_updated: profile.last_updated || profile.datetime_fetched || null,
    datetime_fetched: profile.datetime_fetched || profile.last_updated || null,
  }
  const resolvedMax = resolveMaxWeight(merged)
  if (resolvedMax > 0) {
    merged.informed_max_weight = resolvedMax
  }
  return merged
}

export const enrichSensorsWithFeedData = async (headers, sensors) => {
  if (!Array.isArray(sensors) || sensors.length === 0) return []

  const filtered = filterFeedByVirtual(sensors)
  return Promise.all(filtered.map(sensor => enrichSensorWithStockData(headers, sensor)))
}

export const resolveMaxWeight = (sensor) => {
  const weight = Number(sensor.last_hour_weight ?? sensor.weight ?? 0)
  const level = Number(sensor.last_hour_level ?? sensor.level ?? 0)
  let maxWeight = Number(sensor.informed_max_weight ?? 0)

  if (maxWeight <= 0 && weight > 0 && level > 0) {
    maxWeight = weight / (level / 100)
  }

  return maxWeight
}

export const resolveEffectiveSensorWeight = (sensor) => {
  if (!sensor) return 0

  let weight = Number(sensor.last_hour_weight ?? sensor.weight ?? 0)
  if (weight > 0) return weight

  const level = Number(sensor.last_hour_level ?? sensor.level ?? 0)
  const maxWeight = resolveMaxWeight(sensor)
  if (maxWeight > 0 && level > 0) {
    return maxWeight * (level / 100)
  }

  return 0
}

export const buildSiloStockEntries = (sensors) => {
  if (!Array.isArray(sensors)) return []

  return filterFeedByVirtual(sensors).map(sensor => {
    const weight = resolveEffectiveSensorWeight(sensor)
    const level = Number(sensor.last_hour_level ?? sensor.level ?? 0)
    const maxWeight = resolveMaxWeight(sensor)
    const percentage = maxWeight > 0
      ? Math.round((weight / maxWeight) * 100)
      : (level > 0 ? Math.round(level) : null)

    return {
      siloId: resolveSiloPk(sensor),
      sensorPk: resolveSensorPk(sensor),
      name: sensor.name || sensor.silo_name || `Silo ${resolveSiloPk(sensor)}`,
      weight,
      maxWeight,
      level,
      percentage,
      readingDate: resolveSensorReadingDate(sensor)
    }
  }).sort((a, b) => {
    const numA = parseInt(String(a.name).match(/\d+/)?.[0], 10)
    const numB = parseInt(String(b.name).match(/\d+/)?.[0], 10)
    if (!Number.isNaN(numA) && !Number.isNaN(numB)) return numA - numB
    return String(a.name).localeCompare(String(b.name))
  })
}

export const sumSensorStock = (sensors) => {
  return buildSiloStockEntries(sensors).reduce((sum, entry) => sum + entry.weight, 0)
}

export const sumSensorMaxCapacity = (sensors) => {
  return buildSiloStockEntries(sensors).reduce((sum, entry) => sum + entry.maxWeight, 0)
}

export const resolveBatchBarnPk = (batch, allBarns = []) => {
  const directBarnPk = resolvePk(batch?.barn)
  if (directBarnPk != null) return directBarnPk
  return resolveBarnPkForBatch(batch, allBarns)
}

export const fetchPrioritySensorFeedDataList = async (headers, sensors) => {
  if (!Array.isArray(sensors) || sensors.length === 0) return []

  const filtered = filterFeedByVirtual(sensors)
  const results = await Promise.all(filtered.map(async (sensor) => {
    const enriched = await enrichSensorWithStockData(headers, sensor)
    return resolveEffectiveSensorWeight(enriched) > 0 ? enriched : null
  }))

  return results.filter(Boolean)
}

export const sumPriorityStockFromFeedData = (sensorFeedDataList) => {
  if (!Array.isArray(sensorFeedDataList)) return 0

  return sensorFeedDataList.reduce((total, sensorData) => {
    const weight = Number(sensorData?.last_hour_weight ?? sensorData?.weight ?? 0)
    if (weight > 0) return total + weight
    return total
  }, 0)
}

export const getLocalDateString = (date = new Date()) => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export const calculateBatchAge = (dateAccommodation) => {
  if (!dateAccommodation) return null

  const start = new Date(dateAccommodation)
  if (Number.isNaN(start.getTime())) return null

  const today = new Date()
  start.setHours(0, 0, 0, 0)
  today.setHours(0, 0, 0, 0)

  return Math.floor((today.getTime() - start.getTime()) / 86400000)
}

export const resolveEntryConsume = (entry) => {
  if (!entry) return 0

  let consume = Number(entry.consume ?? 0)

  if (entry.silo_consumes && typeof entry.silo_consumes === 'object') {
    const siloTotal = Object.values(entry.silo_consumes).reduce(
      (sum, value) => sum + Number(value || 0),
      0
    )
    if (siloTotal > consume) consume = siloTotal
  }

  return consume
}

export const resolveLastDayConsume = (consumeData, dateAccommodation) => {
  if (!Array.isArray(consumeData) || !consumeData.length) return 0

  const datedEntries = consumeData
    .filter(entry => entry?.date)
    .sort((a, b) => new Date(b.date) - new Date(a.date))

  for (const entry of datedEntries) {
    const consume = resolveEntryConsume(entry)
    if (consume > 0) return consume
  }

  const batchAge = calculateBatchAge(dateAccommodation)
  if (batchAge != null && batchAge > 0) {
    for (let dayIndex = batchAge - 1; dayIndex >= 0; dayIndex -= 1) {
      const consume = resolveEntryConsume(consumeData[dayIndex])
      if (consume > 0) return consume
    }
  }

  for (let i = consumeData.length - 1; i >= 0; i -= 1) {
    const consume = resolveEntryConsume(consumeData[i])
    if (consume > 0) return consume
  }

  return 0
}

export const resolveCurrentDailyConsume = (consumeData, dateAccommodation) => {
  const completeDays = []
  const batchAge = calculateBatchAge(dateAccommodation)
  const hoje = getLocalDateString()

  if (batchAge != null && batchAge > 0) {
    for (let offset = 1; offset <= 14 && completeDays.length < 3; offset += 1) {
      const dayIndex = batchAge - offset
      if (dayIndex < 0) break

      const consume = resolveEntryConsume(consumeData[dayIndex])
      if (consume > 0) completeDays.push(consume)
    }
  }

  if (completeDays.length < 3) {
    const datedEntries = consumeData
      .filter(entry => entry?.date && String(entry.date).slice(0, 10) < hoje)
      .sort((a, b) => new Date(a.date) - new Date(b.date))

    for (let i = datedEntries.length - 1; i >= 0 && completeDays.length < 3; i -= 1) {
      const consume = resolveEntryConsume(datedEntries[i])
      if (consume > 0) completeDays.push(consume)
    }
  }

  if (!completeDays.length) {
    return resolveEntryConsume(consumeData[consumeData.length - 1])
  }

  return completeDays.reduce((sum, value) => sum + value, 0) / completeDays.length
}

export const resolveLastDailyConsume = (consumeData, dateAccommodation) => {
  return resolveLastDayConsume(consumeData, dateAccommodation)
}

export const calculateFeedDaysRemaining = (stockTotal, consumeData, dateAccommodation) => {
  const stock = Number(stockTotal ?? 0)
  const accommodation = dateAccommodation ?? null
  const consumoUltimoDia = resolveLastDayConsume(consumeData, accommodation)

  if (stock <= 0 || consumoUltimoDia <= 0) return 0

  return Math.round(stock / consumoUltimoDia)
}

export const fetchConsumeByBarnReport = async (
  headers,
  barnPk,
  accommodation,
  departure = null
) => {
  if (barnPk == null || !accommodation) return []

  const acc = String(accommodation).slice(0, 10)
  const dep = String(departure || getLocalDateString()).slice(0, 10)

  try {
    const response = await fetch(
      `${URL}silo_feed_weights/report/consume/${barnPk}/${acc}/${dep}/`,
      { headers }
    )
    if (!response.ok) return []

    const json = await response.json()
    if (!Array.isArray(json?.data)) return []

    return json.data.sort((a, b) => new Date(a.date) - new Date(b.date))
  } catch {
    return []
  }
}

export const getOpenBatchRawFromCachedBarn = (cachedBarnData) => {
  const batches = Array.isArray(cachedBarnData?.batch) ? cachedBarnData.batch : []
  return batches.find(isOpenBatch) || null
}

export const getAnyBatchFromCachedBarn = (cachedBarnData) => {
  const batches = Array.isArray(cachedBarnData?.batch) ? cachedBarnData.batch : []
  return batches.find(batch => batch?.pk != null) || null
}

export const fetchPriorityConsumeData = async ({
  headers,
  batch,
  barnPk,
  allBarns = [],
  cachedBarnData = null
}) => {
  const cachedRawBatch = getOpenBatchRawFromCachedBarn(cachedBarnData)
  let effectiveBatch = batch

  if (cachedRawBatch) {
    effectiveBatch = coerceFeedInventoryBatch(cachedRawBatch, allBarns) || cachedRawBatch
  }

  if (!resolveBatchAccommodationDate(effectiveBatch)) {
    effectiveBatch = getOpenBatchFromCachedBarn(cachedBarnData, allBarns)
      || (barnPk != null ? await fetchOpenBatchForBarn(headers, barnPk, allBarns) : null)
      || effectiveBatch
  }

  const accommodation = resolveBatchAccommodationDate(effectiveBatch)
    ?? resolveBatchAccommodationDate(cachedRawBatch)
  const batchPk = effectiveBatch?.pk
    ?? resolvePk(effectiveBatch)
    ?? resolvePk(cachedRawBatch)
  let consumeData = []

  if (batchPk) {
    consumeData = await fetchConsumeByBatchReport(headers, batchPk)
    if (consumeData.length && resolveLastDayConsume(consumeData, accommodation) > 0) {
      return consumeData
    }
  }

  if (barnPk && accommodation) {
    const departure = effectiveBatch?.date_departure
      ? String(effectiveBatch.date_departure).slice(0, 10)
      : getLocalDateString()
    const byBarn = await fetchConsumeByBarnReport(headers, barnPk, accommodation, departure)
    if (byBarn.length) return byBarn
  }

  return consumeData
}

export const getOpenBatchFromCachedBarn = (cachedBarnData, allBarns = []) => {
  const batches = Array.isArray(cachedBarnData?.batch) ? cachedBarnData.batch : []
  const openBatch = batches.find(isOpenBatch)
  if (!openBatch) return null
  return coerceFeedInventoryBatch(openBatch, allBarns)
}

export const fetchConsumeByBatchReport = async (headers, batchPk) => {
  if (batchPk == null) return []

  const response = await fetch(
    `${URL}silo_feed_weights/report/consume_batch_report/${batchPk}/`,
    { headers }
  )

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`)
  }

  const json = await response.json()
  if (Array.isArray(json?.data)) return json.data
  if (Array.isArray(json)) return json

  return []
}
